import { BadgeCheck, Check, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE, IMAGES } from "../config/site";

const CREDENTIALS = [
  "Psychologist & Child Development Practitioner",
  "Verified counsellor at Mantra Care",
  "Play therapy for children · CBT & client-centred therapy for teens and adults",
  "Individuals, parents, couples & families welcome",
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="bg-[#FDFBF7] py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-8 lg:grid-cols-12 lg:gap-20 lg:px-16">
        <Reveal className="relative lg:col-span-5">
          <div className="overflow-hidden rounded-[2rem] rounded-tr-[6rem] border border-[#064E3B]/10 shadow-[0_30px_60px_-35px_rgba(6,78,59,0.45)]">
            <img
              src={IMAGES.founder}
              alt="Jicy Joy — Founder and psychologist at Little Serein"
              data-testid="founder-portrait-image"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
            />
          </div>
          <a
            href={SITE.mantraCareUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mantra-care-profile-link"
            className="glass-light group absolute -bottom-6 left-6 flex items-center gap-3 rounded-2xl px-5 py-4 shadow-[0_14px_36px_-14px_rgba(6,78,59,0.4)] transition-transform duration-300 hover:-translate-y-1"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#064E3B] text-[#FDFBF7]">
              <BadgeCheck size={20} />
            </span>
            <span>
              <span className="block text-sm font-bold text-[#064E3B]">Verified at Mantra Care</span>
              <span className="flex items-center gap-1 text-xs text-[#526560]">
                View my profile <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </span>
          </a>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Meet your therapist</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-snug tracking-tight text-[#064E3B] sm:text-4xl lg:text-5xl">
              Hi, I&rsquo;m <em className="text-[#059669]">Jicy Joy</em> — founder of Little Serein,
              where therapy speaks your language, at every age.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#526560]">
              &ldquo;Serein&rdquo; is the fine rain that falls from a clear sky — gentle, quiet,
              unexpected. That is how I believe emotional growth happens: softly, safely, and at
              your own pace. With children, I work through play-based therapy, helping them name
              their big feelings. With teens, adults, parents and families, I draw on client-centred
              therapy, CBT and conflict management to move through anxiety, stress, conflict and
              change.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#526560]">
              Whether it is a child who struggles to express, a teen finding their footing, a parent
              who feels lost, or an adult carrying quiet burnout — you don&rsquo;t have to figure it
              out alone.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {CREDENTIALS.map((c) => (
                <li key={c} className="flex items-start gap-2.5 text-sm font-medium text-[#1C2826]">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#ECFDF5] text-[#059669]">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {c}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.28}>
            <a
              href="#contact"
              data-testid="about-book-button"
              className="group mt-9 inline-flex items-center gap-2 rounded-full border border-[#064E3B]/25 px-6 py-3 text-sm font-semibold text-[#064E3B] transition-colors duration-300 hover:bg-[#064E3B] hover:text-[#FDFBF7]"
            >
              Say hello — first discovery call is free
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
